import { ChangeAvatarModal } from '@containers/Layout/AppBar/ChangeAvatarModal';
import { LanguageSelectionModal } from '@containers/Layout/AppBar/LanguageSelectionModal';
import {
  hasNotificationSettings,
  NotificationPreferencesModal,
} from '@containers/Layout/AppBar/NotificationPreferencesModal';
import {
  AuthenticationConnections,
  NavigationStyles,
} from '@generated/graphql';
import { LoginSettingModal } from '@ovComponents/3-pattern/loginSettingModal/loginSettingModal';
import { ViewAllProfilesDialog } from '@ovComponents/3-pattern/viewAllProfilesDialog/viewAllProfilesDialog';
import { CreateEntityWorkflow } from '@ovComponents/4-module/createEntityWorkflow/createEntityWorkflow';
import { AnalyticsContext } from '@providers/analyticsProvider';
import { useAuthContext } from '@providers/ovApolloProvider';
import { UserContext } from '@providers/userContextProvider';
import { ovAnalyticsEvents } from '@utils/analytics/analytics';
import filterHouseholdsWithAccess from '@utils/filterHouseholdsWithAccess';
import { safeWindowOpen } from '@utils/urlSanitization';
import { usePageState, useSearchParams } from '@utils/usePageState';
import { IconButton, Menu, MenuItem, useTheme, useToast } from 'hephaestus/web';
import {
  Language as LanguageIcon,
  Launch as LaunchIcon,
  Logout as LogoutIcon,
  MenuBook as MenuBookIcon,
  NotificationsNone as NotificationsNoneIcon,
  Person,
  AccountCircle as PersonIcon,
  SupportAgent as SupportAgentIcon,
} from 'hephaestus/webIcons';
import {
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLocalization } from '../../../hooks/useLocalization';
import { usePermission } from '../../../hooks/usePermission';
import { EntityList } from './EntityList';

export type AppMenuProp = {
  showOnlyLogout?: boolean;
  children: ReactNode;
};

export const AppMenu = ({ showOnlyLogout, children }: AppMenuProp) => {
  const { translateBackendString } = useLocalization();
  const { appLogout } = useAuthContext();
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { t } = useTranslation(['entityMenu', 'navMenu', 'shared']);
  const { enqueueToast } = useToast();
  const { sendAnalytic } = useContext(AnalyticsContext);
  const canWriteProfile = usePermission('write:organization_user_profile');
  const { userContext, activeEntity, activeHousehold, activeOrganization } =
    useContext(UserContext);

  const [updateLanguageOpen, setUpdateLanguageOpen] = useState(false);
  const [
    updateNotificationPreferencesOpen,
    setUpdateNotificationPreferencesOpen,
  ] = useState(false);
  const [updateAvatarOpen, setUpdateAvatarOpen] = useState(false);
  const [autoOpenWorkflow, setAutoOpenWorkflow] = useState(true);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [loginSettingsOpen, setLoginSettingsOpen] = useState(false);
  const [createEntityOpen, setCreateEntityOpen] = usePageState(
    false,
    'createEntity',
  );
  const [viewAllProfiles, setViewAllProfiles] = useState(false);

  const open = Boolean(anchorEl);

  const autoOpenInParams = params.get('autoOpenWorkflow');

  const allowLogout = useMemo(() => {
    if (
      userContext.organization &&
      activeOrganization.id !== userContext.organization.id
    ) {
      return true;
    }
    return activeOrganization.allowLogout ?? true;
  }, [
    userContext.organization,
    activeOrganization.id,
    activeOrganization.allowLogout,
  ]);

  const entityRedirectPath = useMemo(() => {
    if (
      !activeEntity?.id ||
      userContext.role?.navigationStyle !== NavigationStyles.Simple
    ) {
      return '';
    }

    const households = filterHouseholdsWithAccess(
      activeEntity.households || [],
      activeEntity.id,
      userContext,
    );
    const isHouseholdDefaultView =
      households.length > 0 && userContext.role?.makeHouseholdDefaultView;

    if (
      isHouseholdDefaultView &&
      activeHousehold?.id &&
      (!location.pathname.includes('households') ||
        !location.pathname.includes(activeHousehold.id))
    ) {
      return `/households/${activeHousehold.id}`;
    }

    if (
      !isHouseholdDefaultView &&
      !location.pathname.includes(activeEntity.id)
    ) {
      return `/clients/${activeEntity.id}`;
    }

    return '';
  }, [activeEntity, activeHousehold, userContext, location.pathname]);

  useEffect(() => {
    if (!entityRedirectPath) return;

    if (autoOpenWorkflow || autoOpenInParams) {
      // Sets the autoOpenWorkflow only when initially loading the page for the first entity
      navigate(`${entityRedirectPath}?autoOpenWorkflow=true`);
      setAutoOpenWorkflow(false);
    } else {
      navigate(entityRedirectPath);
    }
  }, [activeEntity, navigate, activeHousehold, userContext]);

  const isUnifiedClientExperience =
    userContext.role?.navigationStyle === NavigationStyles.Simple;

  const handleClose = () => setAnchorEl(null);

  const handleUrlItem = (analyticEvent: string, url: string) => {
    sendAnalytic(analyticEvent);
    handleClose();
    safeWindowOpen(url, {
      target: '_blank',
      onError: (errorMsg) => {
        enqueueToast(errorMsg, { variant: 'error' });
      },
    });
  };

  return (
    <>
      <IconButton
        aria-label='more actions'
        onClick={(event) => {
          sendAnalytic(ovAnalyticsEvents.homepageProfileSelect);
          setAnchorEl(event.currentTarget);
        }}
        data-testid='profile-menu-icon'
        size='small'
        sx={{
          mr: -0.75,
        }}
      >
        <span>{children}</span>
      </IconButton>
      <Menu
        id='profile-menu'
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        sx={{
          '.MuiPaper-root': {
            borderRadius: theme?.component?.entityMenu?.borderRadius,
          },
        }}
        disableScrollLock
      >
        {!showOnlyLogout && isUnifiedClientExperience && (
          <EntityList
            handleClose={handleClose}
            onOpenViewAllProfiles={() => setViewAllProfiles(true)}
            onOpenCreateEntity={() => setCreateEntityOpen(true)}
          />
        )}
        {canWriteProfile && (
          <MenuItem
            data-testid='menuitem-language'
            onClick={() => {
              handleClose();
              setUpdateLanguageOpen(true);
            }}
          >
            <LanguageIcon fontSize='inherit' />
            {t('language')}
          </MenuItem>
        )}
        {activeOrganization.enableLoginSettings &&
          (activeOrganization.defaultAuthenticationConnection !==
            AuthenticationConnections.EmailOtp ||
            activeOrganization.loginUrl) && (
            <MenuItem
              data-testid='menuitem-loginSettings'
              onClick={() => {
                handleClose();

                const { defaultCustodianConnection } = activeOrganization;
                if (
                  defaultCustodianConnection &&
                  'enableCustodianCredentialsManager' in
                    defaultCustodianConnection &&
                  defaultCustodianConnection.enableCustodianCredentialsManager ===
                    true
                ) {
                  const { metadata } = defaultCustodianConnection;
                  const credsAppUrl = metadata?.find(
                    (m) => m.key === 'credentialsManagerUrl',
                  )?.value;
                  if (
                    typeof credsAppUrl === 'string' &&
                    credsAppUrl.trim() !== ''
                  ) {
                    const { protocol, host, pathname } = window.location;
                    const traceId = crypto
                      .randomUUID()
                      .substring(0, 12)
                      .replace(/-/g, '');
                    window.localStorage.setItem(
                      'custodianCredentialsManagerTraceId',
                      traceId,
                    );

                    const inboundRedirectUrl = `${protocol}//${host}${pathname}?custodianmeta=traceId:${traceId}`;
                    const toBeUrlEncoded = `redirect=${inboundRedirectUrl}`;
                    const outboundRedirectUrl = `${credsAppUrl}?${encodeURIComponent(toBeUrlEncoded)}`;
                    safeWindowOpen(outboundRedirectUrl, {
                      target: '_self',
                      onError: (errorMsg) => {
                        enqueueToast(errorMsg, { variant: 'error' });
                      },
                    });
                    return;
                  }
                }

                if (activeOrganization.loginUrl) {
                  safeWindowOpen(activeOrganization.loginUrl, {
                    target: '_self',
                    onError: (errorMsg) => {
                      enqueueToast(errorMsg, { variant: 'error' });
                    },
                  });
                } else {
                  setLoginSettingsOpen(true);
                }
              }}
            >
              <Person fontSize='inherit' />
              {translateBackendString(activeOrganization.loginSettingsTitle)}
            </MenuItem>
          )}
        {canWriteProfile &&
          hasNotificationSettings(userContext.notificationPreferences) && (
            <MenuItem
              data-testid='menuitem-notification-preferences'
              onClick={() => {
                handleClose();
                setUpdateNotificationPreferencesOpen(true);
              }}
            >
              <NotificationsNoneIcon fontSize='inherit' />
              {t('notifications')}
            </MenuItem>
          )}
        {canWriteProfile && (
          <MenuItem
            data-testid='menuitem-updateAvatar'
            onClick={() => {
              handleClose();
              setUpdateAvatarOpen(true);
            }}
          >
            <PersonIcon fontSize='inherit' />
            {t('updateAvatar')}
          </MenuItem>
        )}
        {activeOrganization.enableResourcesAndArticles && (
          <MenuItem
            data-testid='menuitem-resourcesArticles'
            onClick={() => {
              const helpCentreUrl =
                translateBackendString(
                  activeOrganization?.translatedHelpCentreUrl,
                ) || 'https://onevest.zendesk.com/hc/en-us';
              handleUrlItem(
                ovAnalyticsEvents.homepageResourceArticlesSelect,
                helpCentreUrl,
              );
            }}
          >
            <MenuBookIcon fontSize='inherit' />
            {t('resourcesArticles')}
            <LaunchIcon fontSize='inherit' />
          </MenuItem>
        )}
        {activeOrganization.enableContactSupport && (
          <MenuItem
            data-testid='menuitem-contactSupport'
            onClick={() => {
              const supportUrl =
                translateBackendString(
                  activeOrganization?.translatedSupportUrl,
                ) || 'https://onevest.zendesk.com/hc/en-us/requests/new';
              handleUrlItem(
                ovAnalyticsEvents.homepageContactSupportSelect,
                supportUrl,
              );
            }}
          >
            <SupportAgentIcon fontSize='inherit' />
            {t('contactSupport')}
            <LaunchIcon fontSize='inherit' />
          </MenuItem>
        )}
        {allowLogout && (
          <MenuItem
            data-testid='menuitem-logout'
            onClick={() => {
              handleClose();
              void appLogout();
            }}
          >
            <LogoutIcon fontSize='inherit' />
            {t('logout')}
          </MenuItem>
        )}
      </Menu>

      <LoginSettingModal
        open={loginSettingsOpen}
        setOpen={setLoginSettingsOpen}
      />

      <LanguageSelectionModal
        open={updateLanguageOpen}
        setOpen={setUpdateLanguageOpen}
      />

      <ChangeAvatarModal
        open={updateAvatarOpen}
        setOpen={setUpdateAvatarOpen}
      />

      <NotificationPreferencesModal
        open={updateNotificationPreferencesOpen}
        setOpen={setUpdateNotificationPreferencesOpen}
        notificationPreferences={userContext.notificationPreferences}
      />

      <CreateEntityWorkflow
        open={createEntityOpen}
        setOpen={setCreateEntityOpen}
      />

      <ViewAllProfilesDialog
        open={viewAllProfiles}
        onViewDetail={() => setViewAllProfiles(false)}
        onCancel={() => setViewAllProfiles(false)}
        onAddNewProfile={() => {
          setViewAllProfiles(false);
          setCreateEntityOpen(true);
        }}
      />
    </>
  );
};
